"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Search, ArrowRight, Zap, ShieldCheck, Sparkles, MapPin, BarChart3, Wifi, Plane } from "lucide-react";

/** Market-test (market-test-plan.md §3): glass premium design + search-first
 *  glass hero + premium fit-over-price copy (research/07 CF1). Exposure events
 *  carry these ids; the search submit measures search→plan intent. */
const DESIGN_EXPERIMENT_ID = "wf_glass_premium_guided_v1";
const COPY_EXPERIMENT_ID = "copy_premium_fit_vs_trust_v1";
const SEARCH_EXPERIMENT_ID = "wf_homepage_glass_search_v1";

/**
 * AquaGlobe hero — a single large frosted-glass card. Left: a CSS/SVG glass
 * globe-with-orbit motif with floating glass app-icon tiles. Right: a premium,
 * global-category-led headline (strategy.md §8), a destination search row
 * routing to /destinations/, and two sparkle trust chips. Glass-native; no
 * eSIMSUPRA section reuse.
 */
export default function Hero() {
  const { t } = useTranslations();
  const router = useRouter();
  const [q, setQ] = useState("");

  // Page-level exposure for the design + copy experiments (consent-gated,
  // deduped by event_id). research/07 CF1/CF7.
  useEffect(() => {
    for (const id of [DESIGN_EXPERIMENT_ID, COPY_EXPERIMENT_ID]) {
      try {
        track({
          event_name: "page_view",
          event_id: `page_view:${id}`,
          experiment_id: id,
          variant_id: id === COPY_EXPERIMENT_ID ? "premium_fit" : undefined,
        });
      } catch {
        /* analytics is best-effort */
      }
    }
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      track({
        event_name: "destination_search_submitted",
        event_id: `destination_search_submitted:${SEARCH_EXPERIMENT_ID}:${Date.now()}`,
        experiment_id: SEARCH_EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
    router.push("/destinations/");
  };

  // Floating glass app-icon tiles orbiting the globe.
  const tiles = [
    { Icon: MapPin, cls: "top-2 left-6" },
    { Icon: BarChart3, cls: "top-6 right-4" },
    { Icon: Plane, cls: "top-1/2 -left-2 -translate-y-1/2" },
    { Icon: Wifi, cls: "bottom-4 right-8" },
  ];

  return (
    <section className="container mx-auto px-4 pt-10 pb-12 lg:pt-14">
      <div className="aqua-glass rounded-3xl p-6 sm:p-10 backdrop-blur grid lg:grid-cols-2 gap-10 items-center">
        {/* Globe-orbit motif */}
        <div className="relative mx-auto h-64 w-64 sm:h-80 sm:w-80">
          {/* Orbit rings (animated rotation) */}
          <div className="aqua-orbit absolute inset-0 rounded-full border border-primary/25" />
          <div className="aqua-orbit-reverse absolute inset-4 rounded-full border border-secondary/30 [transform:rotateX(62deg)]" />
          <div className="aqua-orbit absolute inset-8 rounded-full border border-primary/20" />
          {/* Globe */}
          <div
            className="absolute inset-10 rounded-full bg-gradient-to-br from-primary/80 via-tertiary/70 to-secondary/70 shadow-[inset_0_8px_24px_-6px_rgba(255,255,255,0.6)] backdrop-blur"
            aria-hidden="true"
          >
            <Wifi className="absolute inset-0 m-auto h-16 w-16 text-white/70" />
          </div>
          {/* Floating glass app-icon tiles */}
          {tiles.map(({ Icon, cls }, i) => (
            <span
              key={i}
              className={`aqua-icon aqua-tile-float absolute ${cls} inline-flex items-center justify-center rounded-2xl p-3 backdrop-blur`}
              style={{ animationDelay: `${i * 0.6}s` }}
              aria-hidden="true"
            >
              <Icon className="h-5 w-5 text-primary" />
            </span>
          ))}
        </div>

        {/* Copy + search */}
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
            {t("aqua.hero.titleLine1", "Connectivity that")}
            <br />
            {t("aqua.hero.titleLine2", "moves with you.")}
          </h1>

          <p className="mt-5 text-lg text-muted-foreground max-w-md">
            {t("aqua.hero.subtitle", "Regional and global eSIM data that fits your trip — chosen the easy way. Beautifully simple, reliably connected.")}
          </p>

          <form onSubmit={onSubmit} className="mt-7 flex items-stretch gap-2 max-w-lg">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                type="text"
                placeholder={t("aqua.hero.searchPlaceholder", "Select Destination (e.g., Japan)")}
                aria-label={t("aqua.hero.searchAria", "Search destinations")}
                className="aqua-glass w-full h-12 rounded-full pl-11 pr-4 text-foreground placeholder:text-muted-foreground backdrop-blur focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center gap-2 h-12 rounded-full bg-primary text-primary-foreground px-6 font-semibold shadow-lg shadow-primary/25 hover:bg-primary/90 transition-all active:scale-[0.98] shrink-0"
            >
              {t("aqua.hero.searchCta", "Find Plans")} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-foreground/85">
            <li className="inline-flex items-center gap-2">
              <Sparkles className="aqua-sparkle h-4 w-4" />
              <Zap className="h-4 w-4 text-primary" />
              {t("aqua.hero.chip1", "Instant Delivery")}
            </li>
            <li className="inline-flex items-center gap-2">
              <Sparkles className="aqua-sparkle h-4 w-4" />
              <ShieldCheck className="h-4 w-4 text-primary" />
              {t("aqua.hero.chip2", "Secure Payment")}
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
